import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type ApiError,
  type GuestCommunicationChannel,
  type GuestCommunicationMessageKey,
  type PropertyCommunicationContent,
  type PropertyCommunicationCatalogResponse,
} from '@/shared/api';
import {
  cloneCatalog,
  mapChannel,
  resetChannel,
  toUpdateRequest,
  withSwitches,
} from './catalogModel';
import { changes, keepLaterEdits } from './editorModel';
import { useCatalogSwitch } from './useCatalogSwitch';

export function useCommunicationCatalog(groupId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = ['property-groups', groupId, 'guest-experience', 'messages'];
  const query = useQuery({
    queryKey,
    queryFn: () => api.propertyGroups.guestExperience.messages.get(groupId),
  });
  const [baseline, setBaseline] = useState<PropertyCommunicationCatalogResponse | null>(null);
  const [draft, setDraft] = useState<PropertyCommunicationCatalogResponse | null>(null);
  const [saveProblem, setSaveProblem] = useState<ApiError | null>(null);
  const [reloadFailed, setReloadFailed] = useState(false);

  const pending = useMemo(
    () => (baseline && draft ? changes(draft, baseline) : []),
    [baseline, draft],
  );
  const dirty = pending.length > 0;

  // Übernommen wird ein neuer Serverstand, nicht jedes Mal, wenn der Entwurf wieder sauber ist —
  // sonst füllte ein gerade geleertes Feld sich mitten im Tippen mit dem Standard.
  const synced = useRef<PropertyCommunicationCatalogResponse | null>(null);
  useEffect(() => {
    if (!query.data || query.data === synced.current || dirty) return;
    synced.current = query.data;
    setBaseline(cloneCatalog(query.data));
    setDraft(cloneCatalog(query.data));
  }, [dirty, query.data]);

  const switches = useCatalogSwitch(groupId, baseline, (saved) => {
    setBaseline(cloneCatalog(saved));
    setDraft((current) => (current ? withSwitches(current, saved) : cloneCatalog(saved)));
    queryClient.setQueryData(queryKey, saved);
  });

  const save = useMutation({
    mutationFn: (sent: PropertyCommunicationCatalogResponse) =>
      api.propertyGroups.guestExperience.messages.update(groupId, toUpdateRequest(sent)),
    networkMode: 'always',
    onMutate: () => {
      setSaveProblem(null);
      setReloadFailed(false);
    },
    onSuccess: (saved, sent) => {
      setBaseline(cloneCatalog(saved));
      setDraft((current) => (current ? keepLaterEdits(current, sent, saved) : cloneCatalog(saved)));
      queryClient.setQueryData(queryKey, saved);
      toast.success('Kommunikation gespeichert');
    },
    onError: async (error) => setSaveProblem(await toApiError(error)),
  });

  const changeChannel = (
    key: GuestCommunicationMessageKey,
    channel: GuestCommunicationChannel,
    patch: Partial<PropertyCommunicationContent>,
  ) => setDraft((current) => mapChannel(current, key, channel, (item) => ({ ...item, ...patch })));

  const restoreChannel = (key: GuestCommunicationMessageKey, channel: GuestCommunicationChannel) =>
    setDraft((current) => mapChannel(current, key, channel, resetChannel));

  const reload = async () => {
    const result = await query.refetch();
    // Scheitert das Laden, bleibt der Entwurf — sonst wäre er weg, ohne dass Neues da ist.
    setReloadFailed(!result.isSuccess);
    if (result.isSuccess) {
      setBaseline(cloneCatalog(result.data));
      setDraft(cloneCatalog(result.data));
      setSaveProblem(null);
      switches.clear();
    }
  };

  const switchConflict = switches.outcome?.kind === 'failed' && switches.outcome.conflict;
  return {
    groupId,
    draft,
    baseline,
    dirty,
    pending,
    conflict: saveProblem?.status === 409 || switchConflict,
    query,
    save,
    submit: (options?: { onSuccess?: () => void }) => draft && save.mutate(draft, options),
    saveProblem,
    reloadFailed,
    changeChannel,
    restoreChannel,
    switches,
    discard: () => {
      if (baseline) setDraft(cloneCatalog(baseline));
      setSaveProblem(null);
    },
    reload,
  };
}
